import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  Backdrop,
  Button,
  CircularProgress,
  ResponsiveTypography,
  Typography,
} from '@achieve/ascend'

import {
  DtiRecommendationBase,
  DtiRadialMeterBase,
  DtiFormInputBase,
  DtiWaitPropsBase,
  DtiTablePropsBase,
} from '../tools'
import { DtiWait } from '../DtiWait'
import { FormIncomeExpenses } from '../FormIncomeExpenses'
import { DtiRadialMeter } from '../DtiRadialMeter'
import { DtiTable } from '../DtiTable'
import { DtiRecommendation } from '../DtiRecommendation'
import { extractTableProps, extractWaitProps } from 'constants/DebtAssessment'

const initialState = {
  loading: false,
  hasResults: false,
  income: 0,
  expenses: 0,
  credit: 0,
  dtiResults: {
    dtiRatio: 0,
    dtiRatioAssessment: '',
  },
  width: 0,
  debtAssessmentData: {},
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_HAS_RESULTS':
      return { ...state, hasResults: action.payload }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_INCOME':
      return { ...state, income: action.payload, changeFormValues: true }
    case 'SET_EXPENSES':
      return { ...state, expenses: action.payload, changeFormValues: true }
    case 'SET_CREDIT':
      return { ...state, credit: action.payload, changeFormValues: true }
    case 'SET_HREF':
      return { ...state, scenarioHref: action.payload }
    case 'SET_DTI_TYPE':
      return { ...state, dtiResults: action.payload }
    case 'SET_DEBT_ASSESSMENT_DATA':
      return { ...state, debtAssessmentData: action.payload }
    case 'SET_WIDTH':
      return { ...state, width: action.payload }
    default:
      return state
  }
}

const LoadingSpinner = (props) => {
  return (
    <Backdrop open={props.isLoading} className="z-50">
      <CircularProgress size={32} />
    </Backdrop>
  )
}

const backgroundMapper = {
  white: '#FFF',
  grey: '#F2F4FE',
  blue: '#30F',
  inherit: 'inherit',
}

const API_TIMEOUT_MS = 20000

const fetchJson = async (url, options) => {
  const response = await fetch(url, { ...options, signal: AbortSignal.timeout(API_TIMEOUT_MS) })
  if (!response.ok) throw new Error(`Status: ${response.status}`)
  return response.json()
}

const eyebrowStyle = { color: '#42546B' }
const resultsContainerStyle = { border: '1px solid #C0CBD8' }

export const CalculatorSection = ({
  title = 'Improve your financial wellness and debt-to-income ratio (DTI)',
  eyebrow = 'DEBT TO INCOME RATIO CALCULATOR',
  eyebrowComponent,
  titleComponent,
  backgroundColor = 'white',
  productType = 'AR',
  dtiTableCtaText = 'Explore debt help options',
  dtiTableCtaUrl = '/get-started?brand=achieve&section=achieve_dr',
  dtiAPiUrl = '/',
  debtAssessmentApiUrl = '/',
  onSubmitFormTracking,
  onChangeParameters,
  onCalculate,
  onTableCtaTracking,
  dtiTableProps = DtiTablePropsBase,
  dtiWaitProps = DtiWaitPropsBase,
  dtiRecommendationProps = DtiRecommendationBase,
  dtiRadialMeterProps = DtiRadialMeterBase,
  dtiFormInputProps = DtiFormInputBase,
  amountParam = 'loan_amount',
  apiErrorMessage = "We couldn't calculate your DTI right now. Please try again in a few minutes.",
}) => {
  const formRef = useRef(null)

  const [state, dispatch] = useReducer(reducer, initialState)
  const { hasResults, loading, income, expenses, credit, dtiResults, debtAssessmentData } = state

  const mapTableProps = () => {
    return Object.keys(debtAssessmentData).length !== 0
      ? {
          ...dtiTableProps,
          ...extractTableProps(debtAssessmentData, productType),
        }
      : { ...dtiTableProps }
  }

  const mapWaitProps = () => {
    return Object.keys(debtAssessmentData).length !== 0
      ? { ...dtiWaitProps, ...extractWaitProps(debtAssessmentData) }
      : { ...dtiWaitProps }
  }

  const onClickCalculate = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    try {
      const data = {
        requester: 'ACHIEVE',
        gross_monthly_income: income,
        monthly_debt_payments: expenses,
        estimated_debt: credit,
      }
      const JSONdata = JSON.stringify(data)
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSONdata,
      }

      setApiError(false)

      // Fetch DTI API data
      let dtiResult = {}
      if (income > 0) {
        dtiResult = await fetchJson(dtiAPiUrl, options)
      }

      // Fetch Debt Assessment API data; it only feeds the table and wait sections, so a failure
      // here shouldn't hide the DTI result
      let debtAssessmentResult = {}
      if (credit >= 5000) {
        debtAssessmentResult = await fetchJson(debtAssessmentApiUrl, options).catch(() => ({}))
      }

      // Handle both API results
      setResult(dtiResult, debtAssessmentResult)
      if (typeof onCalculate === 'function') {
        onCalculate({ dtiResult, debtAssessmentResult })
      }
      // Set loading to false after API responses are processed
      dispatch({ type: 'SET_LOADING', payload: false })
      if (!hasResults) dispatch({ type: 'SET_HAS_RESULTS', payload: true })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false }) // Stop loading even on error
      dispatch({ type: 'SET_HAS_RESULTS', payload: false }) // Prevent showing results on error
      setApiError(true)
    }
  }

  const setResult = (dtiResult, debtAssessmentResult) => {
    // Update the state with the DTI ratio
    dispatch({
      type: 'SET_DTI_TYPE',
      payload: {
        dtiRatio: dtiResult?.dti?.ratio || 0,
        dtiRatioAssessment: dtiResult?.dti?.ratio_assessment
          ? dtiResult.dti.ratio_assessment.toLowerCase()
          : '',
      },
    })

    // Update the state with the debt assessment data for DtiTable and DtiWait
    dispatch({
      type: 'SET_DEBT_ASSESSMENT_DATA',
      payload: debtAssessmentResult,
    })
  }

  const [enableSubmit, setEnableSubmit] = useState(false)
  const [apiError, setApiError] = useState(false)
  useEffect(() => {}, [enableSubmit])

  const backgroundStyle = useMemo(
    () => ({
      background: backgroundMapper[backgroundColor],
    }),
    [backgroundColor]
  )

  return (
    <>
      <div style={backgroundStyle}>
        <div
          className="flex justify-center mb-4 text-center flex-col gap-4"
          data-testid="dti-title-container"
        >
          {eyebrow && (
            <Typography
              style={eyebrowStyle}
              variant="labelOverline"
              fontWeight="bold"
              component={eyebrowComponent}
              data-testid="dti-eyebrow"
            >
              {eyebrow}
            </Typography>
          )}
          {title && (
            <ResponsiveTypography
              variant="headingLg"
              mobileVariant="headingMd"
              fontWeight="bold"
              component={titleComponent}
              data-testid="dti-title"
            >
              {title}
            </ResponsiveTypography>
          )}
        </div>
        <div
          className="mt-8 flex flex-col flex-nowrap bg-inherit lg:flex-row"
          ref={formRef}
          data-testid="dti-container"
        >
          <div className="min-w-[50%]" data-testid="dti-form-container">
            <LoadingSpinner isLoading={loading} data-testid="loading-spinner" />
            <FormIncomeExpenses
              {...dtiFormInputProps}
              enableSubmit={setEnableSubmit}
              onChangeIncome={(value) => {
                dispatch({ type: 'SET_INCOME', payload: value })
                if (typeof onChangeParameters === 'function') {
                  onChangeParameters((prevState) => ({
                    ...prevState,
                    income: value,
                  }))
                }
              }}
              onChangeExpenses={(value) => {
                dispatch({ type: 'SET_EXPENSES', payload: value })
                if (typeof onChangeParameters === 'function') {
                  onChangeParameters((prevState) => ({
                    ...prevState,
                    expenses: value,
                  }))
                }
              }}
              onChangeCredit={(value) => {
                dispatch({ type: 'SET_CREDIT', payload: value })
                if (typeof onChangeParameters === 'function') {
                  onChangeParameters((prevState) => ({
                    ...prevState,
                    credit: value,
                  }))
                }
              }}
              data-testid="dti-form-inputs-container"
            />
            <div className="my-4 w-full lg:w-[170px]">
              <Button
                disabled={!enableSubmit}
                color="primary"
                size="medium"
                variant="contained"
                fullWidth
                onClick={() => {
                  if (onSubmitFormTracking) onSubmitFormTracking()
                  onClickCalculate()
                }}
                data-testid="dti-submit-cta"
              >
                {!hasResults ? 'Calculate' : 'Re-calculate'}
              </Button>
            </div>
            {apiError && (
              <Typography
                variant="bodySm"
                className="text-[#C9252C]"
                role="alert"
                data-testid="dti-api-error"
              >
                {apiErrorMessage}
              </Typography>
            )}
            {hasResults &&
              debtAssessmentData?.projected_assessment &&
              dtiResults.dtiRatioAssessment && (
                <div className="mt-5  justify-start hidden md:block" data-testid="dti-wait">
                  <DtiWait {...mapWaitProps()} dtiRatioAssessment={dtiResults.dtiRatioAssessment} />
                </div>
              )}
          </div>
          <div className="flex min-w-[50%] justify-center" data-testid="dti-results-section">
            <div
              className="flex flex-col justify-center border border-[#C0CBD8] rounded-md w-full lg:w-[520px] min-h-[250px] px-8 h-fit"
              style={resultsContainerStyle}
              data-testid="dti-results-container"
            >
              <DtiRadialMeter {...dtiRadialMeterProps} dtiRatio={dtiResults.dtiRatio} />

              {hasResults && debtAssessmentData?.default_offers && (
                <div data-testid="dti-table-container">
                  <DtiTable {...mapTableProps()} />
                </div>
              )}
              {hasResults && (
                <div
                  className="flex flex-col justify-center"
                  data-testid="dti-recommendations-section"
                >
                  <div
                    className="my-1 flex justify-center w-fit self-center"
                    data-testid="dti-recommendations-button-container"
                  >
                    <Button
                      color="primary"
                      size="medium"
                      variant="contained"
                      fullWidth
                      onClick={onTableCtaTracking && onTableCtaTracking}
                      href={dtiTableCtaUrl + `&${amountParam}=${state.credit}`}
                      data-testid="dti-recommendations-cta"
                    >
                      {dtiTableCtaText}
                    </Button>
                  </div>
                  <hr className="border-0 clear-both block w-[95%] bg-[#c0cbd8] h-px" />
                  {dtiResults.dtiRatio > 0 && (
                    <DtiRecommendation
                      {...dtiRecommendationProps}
                      dtiRatioAssessment={dtiResults.dtiRatioAssessment}
                      credit={state.credit}
                      amountParam={amountParam}
                    />
                  )}
                  {debtAssessmentData?.projected_assessment && dtiResults.dtiRatioAssessment && (
                    <div className="self-center block md:hidden">
                      <DtiWait
                        {...mapWaitProps()}
                        dtiRatioAssessment={dtiResults.dtiRatioAssessment}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
